import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Area, ProjectsViewType } from '@cg-projects/shared';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { AreaSidebar, HelpPopover } from './AreaSidebar';
import { FilterBar } from '@/features/filters/components/FilterBar';
import { Dashboard } from '@/features/dashboard/components/Dashboard';
import { MembersModal } from '@/features/areas/components/MembersModal';
import { UpdatesPanel } from '@/features/areas/components/UpdatesPanel';
import { CreateProjectModal } from '@/features/areas/components/CreateProjectModal';
import { Inbox } from '@/features/inbox/components/Inbox';
import { useUiStore } from '@/core/ui/uiStore';
import { BoardView } from '@/features/board/components/BoardView';
import { TableView } from '@/features/table/components/TableView';
import { CalendarView } from '@/features/calendar/components/CalendarView';
import { TaskDetailPanel } from '@/features/task-detail/components/TaskDetailPanel';
import { Toast } from '@/core/ui/Toast';
import { UpdateBanner } from '@/core/update/UpdateBanner';
import { Spinner } from '@/core/ui/atoms';
import { SearchModal } from '@/features/search/components/SearchModal';
import { SettingsPanel } from '@/features/settings/components/SettingsPanel';
import { MyWork } from '@/features/mywork/components/MyWork';
import { ReportsPage } from '@/features/reports/components/ReportsPage';
import { PacePanel } from '@/features/areas/components/PacePanel';
import { GuidePage } from '@/features/areas/components/GuidePage';
import { RulesPanel } from '@/features/areas/components/RulesPanel';
import { startOutboxDelivery } from '@/core/offline/flush';
import { ShareCapture } from '@/features/tasks/ShareCapture';
import { RequestDoor } from '@/features/requests/RequestDoor';
import { MediaPage } from '@/features/media/MediaPage';
import { BulkBar } from '@/features/tasks/components/BulkBar';
import { ArchivedTasksModal } from '@/features/tasks/components/ArchivedTasksModal';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { joinArea, leaveArea, connectSocket, onUserNotify } from '@/core/realtime/socket';
import { useFilterStore } from '@/features/filters/store/filterStore';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useToastStore } from '@/core/ui/toastStore';
import { api } from '@/core/api/apiClient';
import { HistoryView } from '@/features/history/components/HistoryView';
import type { GroupBy, SortBy } from '@/features/tasks/grouping';
import { startRouter } from '@/core/router/router';
import { installExternalLinkHandler } from '@/core/ui/externalLinks';
import type { LucideIcon } from 'lucide-react';
import { Archive, BookOpen, CalendarDays, Check, Copy, Globe, History, Kanban, List, Lock, Gauge, Megaphone, Menu, MoreHorizontal, Pencil, Pin, Users , Zap } from 'lucide-react';
import { Icon } from '@/core/ui/icons';

const VIEWS: { key: ProjectsViewType; label: string; icon: LucideIcon }[] = [
  { key: 'board', label: 'Board', icon: Kanban },
  { key: 'table', label: 'Table', icon: List },
  { key: 'calendar', label: 'Calendar', icon: CalendarDays },
  { key: 'history', label: 'History', icon: History },
];

// Plain words for the ⋯ menu's arrangement line — its audience is a team lead,
// not a developer, so tokens become words ('due' → 'due date', 'manual' → 'hand').
const GROUP_WORDS: Record<GroupBy, string> = { stage: 'stage', assignee: 'assignee', priority: 'priority', due: 'due date' };
const SORT_WORDS: Record<SortBy, string> = { priority: 'priority', due: 'due date', name: 'name', manual: 'hand' };

/** Board / Table / Calendar / History. Rendered TWICE: inline in the header on
 *  desktop, and as its own full-width row under the header on phones — where it
 *  used to be clipped off the right edge entirely (the header's action cluster was
 *  `shrink-0`, so its `overflow-x-auto` could never scroll and the surplus was just
 *  cut off by the overflow-hidden root). Primary navigation must never live in a
 *  scroll strip. */
function ViewSwitcher({ view, setView, className = '' }: {
  view: ProjectsViewType;
  setView: (v: ProjectsViewType) => void;
  className?: string;
}) {
  return (
    <nav aria-label="Project view" className={`flex items-center gap-0.5 rounded-md border border-line bg-canvas p-0.5 ${className}`}>
      {VIEWS.map((v) => (
        <button
          key={v.key}
          onClick={() => setView(v.key)}
          aria-current={view === v.key ? 'page' : undefined}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition md:flex-none ${
            view === v.key ? 'bg-active text-ink' : 'text-muted hover:text-ink'
          }`}
        >
          <Icon i={v.icon} size={14} />
          {v.label}
        </button>
      ))}
    </nav>
  );
}

function Header({
  area,
  view,
  setView,
  onOpenMembers,
  onOpenUpdates,
  onMenu,
}: {
  area: Area | null;
  view: ProjectsViewType;
  setView: (v: ProjectsViewType) => void;
  onOpenMembers: () => void;
  onOpenUpdates: () => void;
  onMenu: () => void;
}) {
  const user = useAuthStore((s) => s.user);
  const canManage = !!area && !!user && (user.isAdmin || area.managerId === user.uid);
  const applyAreaUpdate = useAreaStore((s) => s.applyAreaUpdate);
  const removeArea = useAreaStore((s) => s.removeArea);
  const toast = useToastStore((s) => s.show);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  // position:fixed — this toolbar scrolls horizontally on phones, and
  // overflow-x:auto CLIPS absolutely-positioned children (invisible menu).
  const [projMenu, setProjMenu] = useState<{ top: number; right: number } | null>(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [paceOpen, setPaceOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  useEffect(() => { setRenaming(false); setProjMenu(null); setArchivedOpen(false); }, [area?.id]);

  const startRename = () => {
    if (!area || !canManage) return;
    setNameDraft(area.name);
    setRenaming(true);
    setProjMenu(null);
  };
  const saveRename = async () => {
    if (!area) return;
    const n = nameDraft.trim();
    setRenaming(false);
    if (!n || n === area.name) return;
    try {
      const { area: updated } = await api.patch<{ area: Area }>(`/areas/${area.id}`, { name: n });
      applyAreaUpdate(updated);
      toast('Project renamed.', 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not rename the project.', 'error');
    }
  };
  const archiveProject = async () => {
    if (!area) return;
    setProjMenu(null);
    if (!window.confirm(`Archive "${area.name}"? It disappears for everyone — an admin can restore it from Settings.`)) return;
    try {
      await api.post(`/areas/${area.id}/archive`, {});
      removeArea(area.id);
      toast('Project archived. Restore it any time from Settings.', 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not archive the project.', 'error');
    }
  };
  const viewLabel = VIEWS.find((v) => v.key === view)?.label ?? view;
  // The live arrangement — what "make this the team default" would publish.
  const groupBy = useFilterStore((s) => s.groupBy);
  const sortBy = useFilterStore((s) => s.sortBy);
  // All three slots must match, and a null/absent stored default matches
  // NOTHING — a project with only a default view saved is not "all set" just
  // because the live arrangement happens to sit on the built-in defaults.
  const isTeamDefault = !!area && area.defaultView === view && area.defaultGroupBy === groupBy && area.defaultSortBy === sortBy;
  const arrangementWords = `${viewLabel} · grouped by ${GROUP_WORDS[groupBy]} · sorted by ${SORT_WORDS[sortBy]}`;
  const setTeamDefault = async () => {
    if (!area || view === 'history') return;
    setProjMenu(null);
    try {
      // ONE write carrying the whole arrangement — view, grouping and sort move
      // together, or a lead could publish a half-updated default.
      const { area: updated } = await api.patch<{ area: Area }>(`/areas/${area.id}`, { defaultView: view, defaultGroupBy: groupBy, defaultSortBy: sortBy });
      applyAreaUpdate(updated);
      toast('New people will now see this project the way you have it set up.', 'info');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not set the team default.', 'error');
    }
  };
  const loadAreas = useAreaStore((s) => s.loadAreas);
  const selectArea = useAreaStore((s) => s.selectArea);
  const [duplicating, setDuplicating] = useState(false);
  const duplicateProject = async () => {
    if (!area || duplicating) return;
    setProjMenu(null);
    const name = window.prompt('Name for the copy (stages, members, and open tasks + their sub-tasks come along; dates are cleared):', `${area.name} (copy)`);
    if (!name?.trim()) return;
    setDuplicating(true); // one copy at a time — an impatient re-click must not fork a second one
    toast('Duplicating — this can take a minute on big projects…', 'info');
    try {
      const { area: copy, skipped } = await api.post<{ area: Area; skipped?: number }>(`/areas/${area.id}/duplicate`, { name: name.trim() }, { timeout: 300000 });
      await loadAreas(true);
      selectArea(copy.id);
      toast(
        skipped
          ? `"${copy.name}" is ready — but ${skipped} item(s) couldn't come along (sub-tasks nested more than one level deep, or a very large project). Check the copy before relying on it.`
          : `"${copy.name}" is ready.`,
        skipped ? 'error' : 'info',
      );
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not duplicate the project.', 'error');
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <header className="flex shrink-0 flex-col border-b border-line">
      <div className="flex h-12 items-center justify-between gap-2 px-3 md:px-4">
      <div className="flex min-w-0 items-center gap-2">
        <button
          onClick={onMenu}
          className="flex shrink-0 items-center rounded-md px-2 py-1 text-muted transition hover:bg-hover hover:text-ink md:hidden"
          title="Menu"
          aria-label="Menu"
        >
          <Icon i={Menu} size={16} />
        </button>
        {area?.isContentTeam && (
          <span className="h-2 w-2 shrink-0 rounded-full bg-brand" title="Content team" />
        )}
        {renaming && area ? (
          <input
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void saveRename(); else if (e.key === 'Escape') setRenaming(false); }}
            onBlur={() => void saveRename()}
            className="min-w-0 rounded border border-brand bg-canvas px-1.5 py-0.5 text-sm font-semibold text-ink outline-none"
          />
        ) : (
          <h1
            onDoubleClick={startRename}
            title={canManage ? 'Double-click to rename' : undefined}
            className="truncate text-sm font-semibold text-ink"
          >
            {area?.name ?? 'CG Projects'}
          </h1>
        )}
        {/* A project whose access list is just its owner. The tooltip states the
            admin exception — a lock icon that overpromises is worse than none. */}
        {area?.isPublic && (
          <span title="Everyone at CG can open this project. Only its members get its notifications."
            className="inline-flex shrink-0 items-center gap-1 rounded border border-line px-1 text-[10px] text-faint"><Icon i={Globe} size={14} /> Everyone</span>
        )}
        {area?.viaReports && (
          <span title="You can open this because someone who reports to you is on it. You can look, not change — ask its lead to add you if you need to work in it."
            className="inline-flex shrink-0 items-center gap-1 rounded border border-line px-1 text-[10px] text-faint"><Icon i={Users} size={14} /> Your people</span>
        )}
        {!area?.isPublic && area?.memberCount !== undefined && area.memberCount <= 1 && (
          <span
            title={area.memberCount === 0
              ? 'No members — only workspace admins, who can see every project, can open this one.'
              : 'Private — only its one member can open this project, plus workspace admins, who can see every project.'}
            className="inline-flex shrink-0 items-center gap-1 rounded border border-line px-1 text-[10px] text-faint"
          >
            <Icon i={Lock} size={14} /> {area.memberCount === 0 ? 'No members' : 'Private'}
          </span>
        )}
      </div>
      {area && (
        // min-w-0 (not shrink-0) is what actually lets this strip SCROLL instead of
        // being clipped by the overflow-hidden root on a narrow screen.
        <div className="flex min-w-0 items-center gap-2 overflow-x-auto scrollbar-hide">
          {/* The per-PROJECT stopwatch that used to live here is gone (Pat, 7/27):
              wrong surface (Odoo's timers were per-task), wrong form (a ticking
              clock in the header), and its time landed only in localStorage —
              invisible to reports or payroll. Time tracking returns as per-task
              start/stop backed by a real time_entries table. */}
          {/* Children must be shrink-0 or they SQUISH instead of scrolling —
              measured: container min-w-0 + shrink-0 children = scrollWidth 436 vs
              clientWidth 238 (scrolls); without it both are 238 (squished). */}
          <div className="shrink-0">
            <button
              onClick={(e) => {
                if (projMenu) { setProjMenu(null); return; }
                const r = e.currentTarget.getBoundingClientRect();
                setProjMenu({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
              }}
              className="flex items-center rounded-md px-2 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink"
              title="Project actions"
              aria-label="Project actions"
            >
              <Icon i={MoreHorizontal} size={14} />
            </button>
            {projMenu && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setProjMenu(null)} />
                {/* w-64 (not w-52): the arrangement item below carries a second
                    descriptive line and must not wrap into a ransom note. */}
                <div style={{ position: 'fixed', top: projMenu.top, right: projMenu.right }} className="z-30 w-64 overflow-hidden rounded-md border border-line bg-canvas shadow-xl">
                  {canManage && (
                    <>
                      <button onClick={startRename} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ink hover:bg-hover"><Icon i={Pencil} size={14} />Rename project</button>
                      <button onClick={() => void duplicateProject()} disabled={duplicating} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ink hover:bg-hover disabled:opacity-50"><Icon i={Copy} size={14} />{duplicating ? 'Duplicating…' : 'Duplicate project…'}</button>
                      {/* The team default arrangement is only the fallback of last
                          resort — setting it never touches anyone's own remembered
                          view, grouping, or sort order. */}
                      {view !== 'history' && (isTeamDefault ? (
                        <button disabled className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-muted disabled:opacity-50"><Icon i={Check} size={14} />Team default ✓</button>
                      ) : (
                        <button onClick={() => void setTeamDefault()} className="w-full px-3 py-2 text-left text-[13px] text-ink hover:bg-hover">
                          <span className="flex items-center gap-2"><Icon i={Pin} size={14} />Make this arrangement the team default</span>
                          <span className="mt-0.5 block pl-[22px] text-[11px] leading-snug text-muted">{arrangementWords}</span>
                        </button>
                      ))}
                    </>
                  )}
                  {/* Phone-reachable Guide entry — the header button is sm+ only. */}
                  <button onClick={() => { setGuideOpen(true); setProjMenu(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ink hover:bg-hover"><Icon i={BookOpen} size={14} />Guide — how we do things…</button>
                  <button onClick={() => { setRulesOpen(true); setProjMenu(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ink hover:bg-hover"><Icon i={Zap} size={14} />What happens by itself…</button>
                  <button onClick={() => { setArchivedOpen(true); setProjMenu(null); }} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ink hover:bg-hover"><Icon i={Archive} size={14} />Archived tasks…</button>
                  {canManage && (
                    <button onClick={() => void archiveProject()} className="flex w-full items-center gap-2 border-t border-line px-3 py-2 text-left text-[13px] text-danger hover:bg-danger/10"><Icon i={Archive} size={14} />Archive project</button>
                  )}
                </div>
              </>
            )}
          </div>
          {archivedOpen && <ArchivedTasksModal areaId={area.id} onClose={() => setArchivedOpen(false)} />}
          <button
            onClick={() => setGuideOpen(true)}
            className="hidden shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink sm:flex"
            title="Guide — how this team does things, living beside the board"
          >
            <Icon i={BookOpen} size={14} />
            Guide
          </button>
          {guideOpen && <GuidePage areaId={area.id} areaName={area.name} onClose={() => setGuideOpen(false)} />}
          {rulesOpen && <RulesPanel areaId={area.id} areaName={area.name} onClose={() => setRulesOpen(false)} />}
          <button
            onClick={() => setPaceOpen(true)}
            className="hidden shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink sm:flex"
            title="Pace — how long work sits in each step here"
          >
            <Icon i={Gauge} size={14} />
            Pace
          </button>
          {paceOpen && <PacePanel areaId={area.id} areaName={area.name} onClose={() => setPaceOpen(false)} />}
          <button
            onClick={onOpenUpdates}
            className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink"
            title="Updates — team-wide announcements for this project"
          >
            <Icon i={Megaphone} size={14} />
            Updates
          </button>
          <button
            onClick={onOpenMembers}
            className="flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink"
            title="Members — who can see this project"
          >
            <Icon i={Users} size={14} />
            Members
          </button>
          <ViewSwitcher view={view} setView={setView} className="hidden shrink-0 md:flex" />
        </div>
      )}
      </div>
      {/* Phones: the view switcher gets its own full-width row so primary navigation
          is always reachable (it used to be clipped off the right edge). */}
      {area && (
        <div className="px-3 pb-2 md:hidden">
          <ViewSwitcher view={view} setView={setView} className="w-full" />
        </div>
      )}
    </header>
  );
}

export function Layout() {
  const areas = useAreaStore((s) => s.areas);
  const loadAreas = useAreaStore((s) => s.loadAreas);
  const selectedAreaId = useAreaStore((s) => s.selectedAreaId);
  const areasLoaded = useAreaStore((s) => s.areasLoaded);
  const areasError = useAreaStore((s) => s.error);
  const view = useAreaStore((s) => s.view);
  const setView = useAreaStore((s) => s.setView);
  const openId = useTaskDetailStore((s) => s.openId);
  const openTask = useTaskDetailStore((s) => s.open);

  // The URL is now a real route: #/ · #/p/<id>/<view> · #/t/<id> ·
  // #/p/<id>/<view>/t/<id>, so Back, refresh and shared links all work. The old
  // pair of hash effects that lived here HAD to go in the same change — its reader
  // closed the open task on every hash change and its writer stripped the whole
  // route whenever no task was open. Two writers over one slot IS the ping-pong.
  // Legacy #t/<id> links (already pasted in chats + exported CSVs) still open.
  //
  // useLayoutEffect, not useEffect: child effects flush BEFORE the parent's, so a
  // cold deep link would otherwise let Dashboard fire a full GET /dashboard (four
  // read_groups against live Odoo) and flash Home chrome for a frame before the
  // route applies. applyRoute never awaits, so the layout phase is safe.
  useLayoutEffect(() => startRouter(), []);
  const createProjectOpen = useUiStore((s) => s.createProjectOpen);
  const inboxOpen = useUiStore((s) => s.inboxOpen);
  const settingsOpen = useUiStore((s) => s.settingsOpen);
  const myWorkOpen = useUiStore((s) => s.myWorkOpen);
  const reportsOpen = useUiStore((s) => s.reportsOpen);
  const mediaOpen = useUiStore((s) => s.mediaOpen);
  const requestDoorOpen = useUiStore((s) => s.requestDoorOpen);
  // The walk-in cooler (v1.84.0): honest about signal, and the outbox
  // delivers itself the moment the network returns.
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    const stopDelivery = startOutboxDelivery();
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); stopDelivery(); };
  }, []);
  const closeRequestDoor = useUiStore((s) => s.closeRequestDoor);
  const closeCreateProject = useUiStore((s) => s.closeCreateProject);
  const [membersOpen, setMembersOpen] = useState(false);
  const [updatesOpen, setUpdatesOpen] = useState(false);
  const searchOpen = useUiStore((s) => s.searchOpen);
  const toggleSearch = useUiStore((s) => s.toggleSearch);
  const closeSearch = useUiStore((s) => s.closeSearch);
  const helpOpen = useUiStore((s) => s.helpOpen);
  const closeHelp = useUiStore((s) => s.closeHelp);
  const [menuOpen, setMenuOpen] = useState(false);

  // Phone drawer: close whenever the user navigates anywhere.
  useEffect(() => { setMenuOpen(false); }, [selectedAreaId, myWorkOpen, inboxOpen, settingsOpen, mediaOpen, requestDoorOpen]);

  // Bulk selection is per-project — switching projects clears it.
  const clearSelected = useTasksStore((s) => s.clearSelected);
  useEffect(() => { clearSelected(); }, [selectedAreaId, clearSelected]);

  // Per-project view/groupBy/sortBy prefs are now written by the USER GESTURES that
  // change them (areaStore.setView → persistPrefs, and the FilterBar controls). The
  // effect that used to live here fired on every commit and could not tell a
  // ViewSwitcher click from a route apply — so opening someone's shared
  // #/p/12/table link permanently rewrote the RECIPIENT's remembered view.
  const groupBy = useFilterStore((s) => s.groupBy);
  const sortBy = useFilterStore((s) => s.sortBy);
  const persistPrefs = useAreaStore((s) => s.persistPrefs);
  useEffect(() => { persistPrefs(); }, [groupBy, sortBy, persistPrefs]);

  useEffect(() => {
    void loadAreas();
  }, [loadAreas]);

  // A user with no projects yet: quietly re-check every 30s so the sidebar
  // fills in the moment an admin adds them (no logout/refresh needed).
  useEffect(() => {
    if (areas.length > 0) return;
    const t = setInterval(() => void loadAreas(true), 30_000);
    return () => clearInterval(t);
  }, [areas.length, loadAreas]);

  // Personal pings land the moment they happen — no waiting on the inbox poll.
  // Bursts collapse into ONE summary toast (a bulk-assign of 25 tasks must not
  // churn 25 toasts and destroy any Undo toast on screen).
  const notifyToast = useToastStore((s) => s.show);
  const openInbox = useUiStore((s) => s.openInbox);
  const burstRef = useRef<{ count: number; timer: ReturnType<typeof setTimeout> | null }>({ count: 0, timer: null });
  useEffect(() => {
    connectSocket(); // even on Home, before any area room is joined
    return onUserNotify((e) => {
      const b = burstRef.current;
      b.count += 1;
      if (b.timer) clearTimeout(b.timer);
      b.timer = setTimeout(() => { b.count = 0; b.timer = null; }, 8000);
      // Background tab? A desktop notification reaches them at their desk.
      // Same-tag notifications COALESCE (a bulk edit must not stack 25 popups),
      // and mid-burst we stop firing entirely.
      if (document.hidden && b.count < 3 && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const dn = new Notification('CG Projects', { body: `${e.text}${e.taskName ? `: ${e.taskName}` : ''}`, tag: 'cg-projects' });
          // Dismiss the notification only once the task is actually open: open()
          // refuses when the reader keeps an unsaved draft, and closing anyway
          // would delete the only remaining pointer to what they were told.
          dn.onclick = () => {
            window.focus();
            if (e.taskId > 0) { void openTask(e.taskId).then((ok) => { if (ok) dn.close(); }); return; }
            dn.close();
          };
        } catch { /* not supported — toasts still cover it */ }
      }
      // The 'added-to-project' side-effect (make the new project appear) MUST
      // run even mid-burst — it's the only path that adds it to the sidebar.
      // Only the toast PRESENTATION collapses in a burst.
      if (e.kind === 'added') void useAreaStore.getState().loadAreas(true);
      // kind 'notify' carries the bell glyph — the message strings stay plain
      // text (Toast renders the icon from structure, not emoji in the copy).
      if (b.count >= 3) {
        notifyToast(`${b.count} new notifications`, 'notify', { label: 'Inbox', run: openInbox });
        return;
      }
      if (e.kind === 'announce') {
        notifyToast(`${e.text}: "${e.taskName}${e.taskName.length >= 60 ? '…' : ''}"`, 'notify', { label: 'Inbox', run: openInbox });
        return;
      }
      if (e.kind === 'added') {
        // loadAreas already fired above; just present the toast + Open action.
        notifyToast(`${e.text} "${e.taskName}"`, 'notify', { label: 'Open', run: () => useAreaStore.getState().selectArea(e.areaId) });
        return;
      }
      notifyToast(`${e.text}: "${e.taskName.length > 45 ? `${e.taskName.slice(0, 45)}…` : e.taskName}"`, 'notify',
        e.taskId > 0 ? { label: 'Open', run: () => void openTask(e.taskId) } : { label: 'My Work', run: () => useUiStore.getState().openMyWork() });
    });
  }, [notifyToast, openTask, openInbox]);

  // Global shortcuts. window+capture per the cg-pos lesson (document-capture
  // stopPropagation swallows bubble listeners). Ctrl/Cmd+K works from anywhere,
  // even mid-typing; every BARE key below it bails while the user is typing or
  // while any modal/panel owns the screen — a shortcut that fires into a form
  // is worse than no shortcut.
  //
  // The listener registers ONCE, so the local overlay flags (members/updates/
  // phone drawer) travel by ref — a dep-array re-register would also drop the
  // g-chord timer mid-chord.
  const overlayRef = useRef(false);
  useEffect(() => { overlayRef.current = membersOpen || updatesOpen || menuOpen; }, [membersOpen, updatesOpen, menuOpen]);
  const gChordRef = useRef(0); // timestamp of the last bare 'g' — arms an 800ms chord window
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggleSearch();
        return;
      }
      // Bare keys only from here down — modified presses belong to the browser.
      // (Shift stays allowed: '?' IS Shift+/ on a US keyboard.)
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      const ui = useUiStore.getState();
      // '?' toggles the tips popover from ANY screen — checked BEFORE the modal
      // bail (help must be reachable from My Work / Inbox / an open task too;
      // the popover renders at Layout level, last of the z-50 surfaces, so it
      // paints above whatever it was opened over) and BEFORE the helpOpen bail
      // so the same key that opened it closes it.
      if (e.key === '?') {
        e.preventDefault();
        ui.toggleHelp();
        return;
      }
      const modalOpen = ui.createProjectOpen || ui.requestDoorOpen || ui.inboxOpen || ui.settingsOpen || ui.myWorkOpen
        || ui.reportsOpen || ui.mediaOpen || ui.searchOpen || overlayRef.current
        || useTaskDetailStore.getState().openId !== null;
      if (modalOpen) { gChordRef.current = 0; return; }
      if (ui.helpOpen) return;
      // g-chord: 'g' arms an 800ms window; i/m/h/r/d inside it navigates. Any
      // other key cancels the chord and falls through to be handled normally.
      if (gChordRef.current && Date.now() - gChordRef.current < 800) {
        gChordRef.current = 0;
        const k = e.key.toLowerCase();
        if (k === 'i') { e.preventDefault(); ui.openInbox(); return; }
        if (k === 'm') { e.preventDefault(); ui.openMyWork(); return; }
        if (k === 'h') { e.preventDefault(); useAreaStore.getState().goHome(); return; }
        if (k === 'r') { e.preventDefault(); ui.openReports(); return; }
        if (k === 'd') { e.preventDefault(); ui.openMedia(); return; } // d for drive/media — 'm' is taken by My Work
      }
      if (e.key === 'g' || e.key === 'G') { gChordRef.current = Date.now(); return; }
      if (e.key === '/') {
        // Jump to the on-screen FilterBar search; when no board is on screen
        // (Home), the element is absent and global search is the next best door.
        e.preventDefault();
        const el = document.getElementById('cgp-filter-search');
        if (el instanceof HTMLInputElement) { el.focus(); el.select(); }
        else ui.openSearch();
        return;
      }
      if (e.key === 'c' || e.key === 'C') {
        // New task — only when a project board is the active view. Emits a
        // uiStore signal; BoardView opens the first visible stage's composer
        // from it (see composerRequest in uiStore).
        const a = useAreaStore.getState();
        if (a.view === 'board' && a.areas.some((ar) => ar.id === a.selectedAreaId)) {
          e.preventDefault();
          ui.requestComposer();
        }
        return;
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // External links inside rendered rich bodies (.cg-prose — comments, notes,
  // descriptions) open in a new tab; same-origin app links keep navigating in
  // place. One delegated document listener, registered once like the keymap.
  useEffect(() => installExternalLinkHandler(), []);

  const area = areas.find((a) => a.id === selectedAreaId) ?? null;

  // Live updates: follow the on-screen project's room. Gate on the RESOLVED area,
  // not the raw id — a deep-linked id we can't see must never emit a join. Keyed on
  // the ID, not the object: `area` is an unmemoized find() over a list every
  // loadAreas replaces wholesale, so an object dep would leave→rejoin the room on
  // every 30s poll and every socket-driven reload. join:area is async and silently
  // refuses on an access-check throw, so a needless churn can leave the board dead.
  const areaId = area?.id ?? null;
  useEffect(() => {
    if (areaId === null) return;
    joinArea(areaId);
    return () => leaveArea(areaId);
  }, [areaId]);

  // A URL can name a project you can't open — a stale bookmark, a link shared by
  // someone with more access, or Back into a project whose access was just
  // revoked. Once /areas has ANSWERED and it still isn't there, say so and go
  // Home. FromRoute ⇒ replace, so Back can't walk back into it, and this can't loop.
  useEffect(() => {
    if (!areasLoaded || selectedAreaId === null) return;
    if (areas.some((a) => a.id === selectedAreaId)) return;
    // areasLoaded is also true when the FETCH FAILED. "Not available to you" would
    // then be a lie — the honest answer is "we couldn't load your projects". Say
    // that instead, and don't send them Home (the link may be perfectly good).
    if (areasError) {
      useToastStore.getState().show("Couldn't load your projects — check your connection and reload.", 'error');
      return;
    }
    useToastStore.getState().show("That project isn't available to you — it may have been archived, made members-only, or you were removed.", 'info');
    useAreaStore.getState().goHomeFromRoute();
  }, [areasLoaded, areasError, areas, selectedAreaId]);


  return (
    <div className="flex h-dvh overflow-hidden bg-page text-ink">
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <AreaSidebar />
      </div>
      {/* Phone drawer */}
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setMenuOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-50 shadow-2xl md:hidden">
            <AreaSidebar />
          </div>
        </>
      )}
      <main className="flex min-w-0 flex-1 flex-col">
        <Header area={area} view={view} setView={setView} onOpenMembers={() => setMembersOpen(true)} onOpenUpdates={() => setUpdatesOpen(true)} onMenu={() => setMenuOpen(true)} />
        {area && view !== 'history' && <FilterBar />}
        <div className="min-h-0 flex-1 overflow-hidden">
          {/* A deep link names a project BEFORE /areas has answered. Show a neutral
              wait — never the Dashboard, which fires its own fetch and can render
              "You don't have any projects yet" at someone who does. */}
          {!area && selectedAreaId !== null && !areasLoaded ? (
            <div className="flex h-full items-center justify-center"><Spinner label="Opening project…" /></div>
          ) : !area ? (
            <Dashboard />
          ) : view === 'board' ? (
            <BoardView areaId={area.id} />
          ) : view === 'table' ? (
            <TableView areaId={area.id} />
          ) : view === 'history' ? (
            <HistoryView areaId={area.id} />
          ) : (
            <CalendarView areaId={area.id} dateField={area.calendarDateField} />
          )}
        </div>
      </main>
      {area && <BulkBar areaId={area.id} />}
      {openId !== null && <TaskDetailPanel />}
      {membersOpen && area && <MembersModal area={area} onClose={() => setMembersOpen(false)} />}
      {updatesOpen && area && <UpdatesPanel area={area} onClose={() => setUpdatesOpen(false)} />}
      {createProjectOpen && <CreateProjectModal onClose={closeCreateProject} />}
      {inboxOpen && <Inbox />}
      {settingsOpen && <SettingsPanel />}
      {myWorkOpen && <MyWork />}
      {requestDoorOpen && <RequestDoor onClose={closeRequestDoor} />}
      {offline && (
        <div className="fixed inset-x-0 top-0 z-[90] bg-warn px-3 py-1 text-center text-[12px] font-medium text-page">
          No signal — showing what you last loaded. Notes you send will wait and go when you’re back.
        </div>
      )}
      <ShareCapture />
      {reportsOpen && <ReportsPage />}
      {mediaOpen && <MediaPage />}
      {searchOpen && <SearchModal onClose={closeSearch} />}
      {/* Tips & shortcuts — rendered HERE, not inside AreaSidebar: the sidebar
          is display:none'd below md (fixed-position children of a display:none
          ancestor paint nothing), which made '?' toggle an invisible popover on
          phones. Mounted after every panel so equal z-50 resolves in its favor. */}
      {helpOpen && <HelpPopover onClose={closeHelp} />}
      <Toast />
      {/* An open tab runs the code it loaded forever; this is the only way anyone
          learns a fix shipped. It never reloads by itself. */}
      <UpdateBanner />
    </div>
  );
}
